import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { faCirclePlus, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Box,
    Button,
    FormGroup,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import ControlWithLabel from 'src/components/ControlWithLabel';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledChip from 'src/components/StyledChip';
import StyledIconButton from 'src/components/StyledIconButton';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import VisitSummaryDisplay from 'src/components/VisitSummaryDisplay';
import ConfirmationModal from 'src/components/modals/ConfirmationModal';
import { useModalManager } from 'src/components/modals/ModalManager';
import {
    CareRecipientObservationIcons,
    CareRecipientObservationLabels,
    CareRecipientObservations,
    MoodDescriptorLabels,
    MoodDescriptors,
    Moods,
    ResidenceObservationIcons,
    ResidenceObservationLabels,
    ResidenceObservations,
    VisitMoodIcons,
    VisitMoodLabels,
} from 'src/constants/Visit';
import { AnalyticsContext } from 'src/contexts/AnalyticsContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import {
    CareRecipientObservation,
    CareRecipientObservationEnum,
    Mood,
    MoodDescriptor,
    ResidenceObservation,
    ResidenceObservationEnum,
    useCreateRequestsMutation,
    useCreateVisitSummaryMutation,
} from 'src/store/sageHomeApi';
import { getTeamMemberDisplayName } from 'src/utils/nameUtils';

const summarySearchSchema = z.object({
    visitSummaryRid: z.string().optional(),
    requestRid: z.string().optional(),
});

export const Route = createFileRoute('/(app)/visits/summary/add/$visitRid')({
    component: AddVisitSummaryComponent,
    validateSearch: zodValidator(summarySearchSchema),
});

const PageName = 'Add Care Summary';

// Mood options
const moodOptions = [
    { value: Moods.GREAT, label: VisitMoodLabels.GREAT },
    { value: Moods.GOOD, label: VisitMoodLabels.GOOD },
    { value: Moods.OKAY, label: VisitMoodLabels.OKAY },
    { value: Moods.NOT_GOOD, label: VisitMoodLabels.NOT_GOOD },
];

// Mood descriptor options
const moodDescriptorOptions = [
    {
        value: MoodDescriptors.MORE_CONFUSED_THAN_USUAL,
        label: MoodDescriptorLabels.MORE_CONFUSED_THAN_USUAL,
    },
    {
        value: MoodDescriptors.MORE_TIRED_THAN_USUAL,
        label: MoodDescriptorLabels.MORE_TIRED_THAN_USUAL,
    },
    {
        value: MoodDescriptors.MORE_AGITATED_THAN_USUAL,
        label: MoodDescriptorLabels.MORE_AGITATED_THAN_USUAL,
    },
    { value: MoodDescriptors.MORE_SAD_THAN_USUAL, label: MoodDescriptorLabels.MORE_SAD_THAN_USUAL },
];

// Care recipient observation options
const careRecipientObservationOptions = [
    {
        value: CareRecipientObservations.SKIN_ISSUE,
        label: CareRecipientObservationLabels.SKIN_ISSUE,
    },
    {
        value: CareRecipientObservations.INCREASED_PAIN,
        label: CareRecipientObservationLabels.INCREASED_PAIN,
    },
    {
        value: CareRecipientObservations.BEHAVIORAL_ISSUES,
        label: CareRecipientObservationLabels.BEHAVIORAL_ISSUES,
    },
    {
        value: CareRecipientObservations.ABNORMAL_URINE,
        label: CareRecipientObservationLabels.ABNORMAL_URINE,
    },
    {
        value: CareRecipientObservations.ABNORMAL_BOWEL_MOVEMENTS,
        label: CareRecipientObservationLabels.ABNORMAL_BOWEL_MOVEMENTS,
    },
    {
        value: CareRecipientObservations.ATE_LESS_THAN_FIFTY_PERCENT_OF_MEAL,
        label: CareRecipientObservationLabels.ATE_LESS_THAN_FIFTY_PERCENT_OF_MEAL,
    },
    {
        value: CareRecipientObservations.NEEDS_MORE_HELP_THAN_USUAL,
        label: CareRecipientObservationLabels.NEEDS_MORE_HELP_THAN_USUAL,
    },
];

// Residence observation options
const residenceObservationOptions = [
    {
        value: ResidenceObservations.INTERIOR_MAINTENANCE_NEEDED,
        label: ResidenceObservationLabels.INTERIOR_MAINTENANCE_NEEDED,
    },
    {
        value: ResidenceObservations.EXTERIOR_MAINTENANCE_NEEDED,
        label: ResidenceObservationLabels.EXTERIOR_MAINTENANCE_NEEDED,
    },
];

const requestSuggestions: string[] = ['Trash Bags', 'Gloves', 'Paper Towels', 'Tissues'];

export type StepId =
    | 'observations'
    | 'observationNotes'
    | 'residenceObservationNotes'
    | 'mood'
    | 'moodDescriptors'
    | 'summaryNotes'
    | 'supplyRequest'
    | 'confirm';

const orderedSteps: StepId[] = [
    'observations',
    'observationNotes',
    'residenceObservationNotes',
    'mood',
    'moodDescriptors',
    'summaryNotes',
    'supplyRequest',
    'confirm',
];

interface AddVisitSummaryFormData {
    // Visit Summary fields
    mood?: Mood;
    moodDescriptors?: MoodDescriptor[];
    careRecipientObservations?: CareRecipientObservation[];
    residenceObservations?: ResidenceObservation[];
    summaryNotes?: string;
    // Request fields
    requestDescription?: string;
}

function AddVisitSummaryComponent() {
    const { trackPageView } = useContext(AnalyticsContext);
    const { showSuccess, showError } = useSnackbar();
    const navigate = useNavigate();
    const { careRecipient } = useTeamMemberData();
    const { palette, spacing } = useTheme();
    const { visitRid } = useParams({ from: '/(app)/visits/summary/add/$visitRid' });
    const careRecipientDisplayName = getTeamMemberDisplayName(careRecipient, { firstOnly: true });
    const { openModal } = useModalManager();
    const [currentStepId, setCurrentStepId] = useState<StepId>('observations');
    const [supplyRequests, setSupplyRequests] = useState<
        Array<{
            requestType: 'SUPPLY';
            status: 'NEEDED';
            requestDescription: string;
            visitRid: string;
        }>
    >([]);
    const [currentRequestDescription, setCurrentRequestDescription] = useState<string>('');
    const [createVisitSummary, { isLoading: isCreatingSummary }] = useCreateVisitSummaryMutation();
    const [createRequest, { isLoading: isCreatingRequest }] = useCreateRequestsMutation();

    const isLoading = isCreatingSummary || isCreatingRequest;

    const { control, handleSubmit, watch, setValue } = useForm<AddVisitSummaryFormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        defaultValues: {
            mood: undefined,
            moodDescriptors: [],
            careRecipientObservations: [],
            residenceObservations: [],
            summaryNotes: undefined,
            requestDescription: undefined,
        },
    });

    // Watch all form values at once
    const formValues = watch();

    // Check if form has any changes from default values
    const isFormDirty = useMemo(() => {
        const hasMood = !!formValues.mood;
        const hasMoodDescriptors = (formValues.moodDescriptors?.length ?? 0) > 0;
        const hasCareRecipientObservations =
            (formValues.careRecipientObservations?.length ?? 0) > 0;
        const hasResidenceObservations = (formValues.residenceObservations?.length ?? 0) > 0;
        const hasSummaryNotes = !!formValues.summaryNotes?.trim();
        const hasRequestDescription = !!formValues.requestDescription?.trim();

        return (
            hasMood ||
            hasMoodDescriptors ||
            hasCareRecipientObservations ||
            hasResidenceObservations ||
            hasSummaryNotes ||
            hasRequestDescription ||
            supplyRequests.length > 0
        );
    }, [formValues, supplyRequests.length]);

    // Compute all validation and visibility states using useMemo
    const formValidation = useMemo(() => {
        const {
            mood,
            careRecipientObservations,
            residenceObservations,
            summaryNotes,
            requestDescription,
            moodDescriptors,
        } = formValues;

        // Helper function to check if text input has content
        const hasTextInput = (value?: string) => value && value.trim() !== '';

        // Helper function to check if array has items
        const hasArrayItems = (value?: unknown[]) => value && value.length > 0;

        // Check if observations have notes
        const hasCareRecipientObservationNotes =
            careRecipientObservations?.some((obs) => obs.observationNotes?.trim()) || false;
        const hasResidenceObservationNotes =
            residenceObservations?.some((obs) => obs.observationNotes?.trim()) || false;

        return {
            // Visibility flags
            showMoodDescriptors: mood === Moods.NOT_GOOD,
            showObservationNotes: hasArrayItems(careRecipientObservations),
            showResidenceObservationNotes: hasArrayItems(residenceObservations),
            showResidenceNotes: hasArrayItems(residenceObservations),

            // Validation flags
            hasMood: !!mood,
            hasSummaryInput: hasTextInput(summaryNotes),
            hasRequestsInput: hasTextInput(requestDescription),
            hasCareRecipientObservationNotesInput: hasCareRecipientObservationNotes,
            hasResidenceObservationNotesInput: hasResidenceObservationNotes,
            hasMoodDescriptors: hasArrayItems(moodDescriptors),
        };
    }, [formValues]);

    const stepVisibility = useMemo(
        () => ({
            observations: true,
            observationNotes: formValidation.showObservationNotes,
            residenceObservationNotes: formValidation.showResidenceObservationNotes,
            mood: true,
            moodDescriptors: formValidation.showMoodDescriptors,
            summaryNotes: true,
            supplyRequest: true,
            confirm: true,
        }),
        [
            formValidation.showMoodDescriptors,
            formValidation.showObservationNotes,
            formValidation.showResidenceObservationNotes,
        ]
    );

    // Get color for each mood
    const getMoodColor = (mood: Mood) => {
        switch (mood) {
            case Moods.GREAT:
                return palette.green?.[700];
            case Moods.GOOD:
                return palette.green?.[800];
            case Moods.OKAY:
                return palette.orange?.[900];
            case Moods.NOT_GOOD:
                return palette.red?.[950];
            default:
                return palette.green?.[700];
        }
    };

    const visibleSteps = useMemo(
        () => orderedSteps.filter((step) => stepVisibility[step]),
        [stepVisibility]
    );

    const currentStepIndex = useMemo(
        () => visibleSteps.findIndex((step) => step === currentStepId),
        [currentStepId, visibleSteps]
    );

    const totalSteps = visibleSteps.length;

    const handleNextStep = useCallback(() => {
        if (!visibleSteps.length) return;
        const nextIndex = currentStepIndex + 1;
        if (nextIndex < visibleSteps.length) {
            setCurrentStepId(visibleSteps[nextIndex]);
        }
    }, [currentStepIndex, visibleSteps]);

    const canGoBack = useMemo(() => currentStepIndex > 0, [currentStepIndex]);

    const handlePreviousStep = useCallback(() => {
        if (!visibleSteps.length) return;
        const prevIndex = currentStepIndex - 1;
        if (prevIndex >= 0) {
            setCurrentStepId(visibleSteps[prevIndex]);
        }
    }, [currentStepIndex, visibleSteps]);

    const handleAddRequest = (requestDescription?: string) => {
        const description = requestDescription?.trim() || currentRequestDescription.trim();
        if (description && visitRid) {
            setSupplyRequests((currentRequests) => {
                const isDuplicate = currentRequests.some(
                    (req) => req.requestDescription.toLowerCase() === description.toLowerCase()
                );
                if (isDuplicate) {
                    return currentRequests;
                }
                return [
                    ...currentRequests,
                    {
                        requestType: 'SUPPLY',
                        status: 'NEEDED',
                        requestDescription: description,
                        visitRid,
                    },
                ];
            });

            // Clear input field if we used it (no parameter provided)
            if (!requestDescription) {
                setCurrentRequestDescription('');
            }
        }
    };

    const handleCloseClick = () => {
        if (isFormDirty) {
            openModal({
                content: ConfirmationModal,
                props: {
                    onConfirm: () => navigate({ to: '/visits' }),
                    title: 'You have unsaved changes that will be lost. Are you sure?',
                    confirmText: 'Continue',
                    cancelText: 'Go Back',
                },
            });
        } else {
            navigate({ to: '/visits' });
        }
    };

    const renderStep = (stepId: StepId) => {
        switch (stepId) {
            case 'observations':
                return (
                    <>
                        <Stack sx={{ gap: 4 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                Did you notice any of the below during your visit?
                            </Typography>

                            <Stack sx={{ gap: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {getTeamMemberDisplayName(careRecipient, {
                                        firstOnly: true,
                                        usePreferred: true,
                                    })}
                                    :
                                </Typography>
                                <Controller
                                    name="careRecipientObservations"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <FormGroup sx={{ gap: 2 }}>
                                            {careRecipientObservationOptions.map((option) => {
                                                const currentObservations = Array.isArray(value)
                                                    ? value
                                                    : [];
                                                const isChecked = currentObservations.some(
                                                    (obs) => obs.observationValue === option.value
                                                );
                                                const IconComponent =
                                                    CareRecipientObservationIcons[option.value];
                                                return (
                                                    <ControlWithLabel
                                                        key={option.value}
                                                        label={option.label}
                                                        icon={
                                                            IconComponent ? (
                                                                <IconComponent />
                                                            ) : undefined
                                                        }
                                                        checked={isChecked}
                                                        controlType="checkbox"
                                                        onChange={(e) => {
                                                            const current = Array.isArray(value)
                                                                ? value
                                                                : [];
                                                            if (e.target.checked) {
                                                                onChange([
                                                                    ...current,
                                                                    {
                                                                        observationValue:
                                                                            option.value as CareRecipientObservationEnum,
                                                                    },
                                                                ]);
                                                            } else {
                                                                onChange(
                                                                    current.filter(
                                                                        (obs) =>
                                                                            obs.observationValue !==
                                                                            option.value
                                                                    )
                                                                );
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                    )}
                                />
                            </Stack>
                            <Stack sx={{ gap: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    The residence:
                                </Typography>
                                <Controller
                                    name="residenceObservations"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <FormGroup sx={{ gap: 2 }}>
                                            {residenceObservationOptions.map((option) => {
                                                const currentObservations = Array.isArray(value)
                                                    ? value
                                                    : [];
                                                const isChecked = currentObservations.some(
                                                    (obs) => obs.observationValue === option.value
                                                );
                                                const IconComponent =
                                                    ResidenceObservationIcons[option.value];
                                                return (
                                                    <ControlWithLabel
                                                        key={option.value}
                                                        label={option.label}
                                                        icon={
                                                            IconComponent ? (
                                                                <IconComponent />
                                                            ) : undefined
                                                        }
                                                        checked={isChecked}
                                                        controlType="checkbox"
                                                        onChange={(e) => {
                                                            const current = Array.isArray(value)
                                                                ? value
                                                                : [];
                                                            if (e.target.checked) {
                                                                onChange([
                                                                    ...current,
                                                                    {
                                                                        observationValue:
                                                                            option.value as ResidenceObservationEnum,
                                                                    },
                                                                ]);
                                                            } else {
                                                                onChange(
                                                                    current.filter(
                                                                        (obs) =>
                                                                            obs.observationValue !==
                                                                            option.value
                                                                    )
                                                                );
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                    )}
                                />
                            </Stack>
                        </Stack>

                        <PageFooter includeShadow>
                            <Button
                                type="button"
                                variant={
                                    formValidation.showObservationNotes ||
                                    formValidation.showResidenceObservationNotes
                                        ? 'contained'
                                        : 'text'
                                }
                                color="primary"
                                onClick={handleNextStep}
                            >
                                {formValidation.showObservationNotes ||
                                formValidation.showResidenceObservationNotes
                                    ? 'Continue'
                                    : 'None of the above'}
                            </Button>
                        </PageFooter>
                    </>
                );
            case 'observationNotes': {
                const careRecipientObs = formValues.careRecipientObservations || [];
                const currentNotes = careRecipientObs[0]?.observationNotes || '';

                const handleNotesChange = (notes: string) => {
                    // Update notes on all care recipient observations
                    if (careRecipientObs.length > 0) {
                        setValue(
                            'careRecipientObservations',
                            careRecipientObs.map((obs) => ({
                                ...obs,
                                observationNotes: notes || undefined,
                            }))
                        );
                    }
                };

                const hasNotes = currentNotes.trim() !== '';

                return (
                    <>
                        <Stack sx={{ gap: 3 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                Describe what you observed:
                            </Typography>
                            {careRecipientObs.length > 0 && (
                                <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                                    {careRecipientObs.map((obs, index) => (
                                        <StyledChip
                                            key={index}
                                            label={
                                                CareRecipientObservationLabels[obs.observationValue]
                                            }
                                        />
                                    ))}
                                </Stack>
                            )}
                            <TextField
                                value={currentNotes}
                                onChange={(e) => handleNotesChange(e.target.value)}
                                multiline
                                rows={3}
                                placeholder="Add notes"
                                fullWidth
                            />
                        </Stack>
                        <PageFooter includeShadow>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleNextStep}
                                disabled={!hasNotes}
                            >
                                Continue
                            </Button>
                        </PageFooter>
                    </>
                );
            }
            case 'residenceObservationNotes': {
                const residenceObs = formValues.residenceObservations || [];
                const currentNotes = residenceObs[0]?.observationNotes || '';

                const handleNotesChange = (notes: string) => {
                    // Update notes on all residence observations
                    if (residenceObs.length > 0) {
                        setValue(
                            'residenceObservations',
                            residenceObs.map((obs) => ({
                                ...obs,
                                observationNotes: notes || undefined,
                            }))
                        );
                    }
                };

                const hasNotes = currentNotes.trim() !== '';

                return (
                    <>
                        <Stack sx={{ gap: 3 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                Describe what you observed:
                            </Typography>
                            {residenceObs.length > 0 && (
                                <Stack direction="row" sx={{ gap: 1 }}>
                                    {residenceObs.map((obs, index) => (
                                        <StyledChip
                                            key={index}
                                            label={ResidenceObservationLabels[obs.observationValue]}
                                        />
                                    ))}
                                </Stack>
                            )}
                            <TextField
                                value={currentNotes}
                                onChange={(e) => handleNotesChange(e.target.value)}
                                multiline
                                rows={3}
                                placeholder="Add notes"
                                fullWidth
                            />
                        </Stack>
                        <PageFooter includeShadow>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleNextStep}
                                disabled={!hasNotes}
                            >
                                Continue
                            </Button>
                        </PageFooter>
                    </>
                );
            }
            case 'mood':
                return (
                    <>
                        <Stack sx={{ gap: 2 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                How was the {careRecipientDisplayName} during the visit?
                            </Typography>
                            <Controller
                                name="mood"
                                control={control}
                                shouldUnregister={false}
                                rules={{ required: 'Please select a mood' }}
                                render={({ field: { onChange, value } }) => (
                                    <FormGroup sx={{ gap: 2 }}>
                                        {moodOptions.map((option) => {
                                            const IconComponent = VisitMoodIcons[option.value];
                                            const isChecked = value === option.value;

                                            return (
                                                <ControlWithLabel
                                                    key={option.value}
                                                    label={option.label}
                                                    icon={
                                                        IconComponent ? (
                                                            <FontAwesomeIcon
                                                                icon={IconComponent}
                                                                size="2x"
                                                                style={{
                                                                    color: getMoodColor(
                                                                        option.value
                                                                    ),
                                                                }}
                                                            />
                                                        ) : undefined
                                                    }
                                                    checked={isChecked}
                                                    controlType="radio"
                                                    onChange={() => {
                                                        onChange(option.value);
                                                        if (option.value !== Moods.NOT_GOOD) {
                                                            setValue('moodDescriptors', []);
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                )}
                            />
                        </Stack>
                        <PageFooter includeShadow>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleNextStep}
                                disabled={!formValidation.hasMood}
                            >
                                Continue
                            </Button>
                        </PageFooter>
                    </>
                );
            case 'moodDescriptors':
                return (
                    <>
                        <Stack sx={{ gap: 2 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                {careRecipientDisplayName} was:
                            </Typography>
                            <Controller
                                name="moodDescriptors"
                                control={control}
                                shouldUnregister={false}
                                render={({ field: { onChange, value } }) => {
                                    // If value is "NOT_GOOD", value || [] = "NOT_GOOD" (not [])
                                    // We must check if it's actually an array
                                    const safeValue = Array.isArray(value) ? value : [];

                                    return (
                                        <FormGroup sx={{ gap: 2 }}>
                                            {moodDescriptorOptions.map((option) => {
                                                const isChecked = safeValue.includes(
                                                    option.value as MoodDescriptor
                                                );
                                                return (
                                                    <ControlWithLabel
                                                        key={option.value}
                                                        label={option.label}
                                                        checked={isChecked}
                                                        controlType="checkbox"
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                onChange([
                                                                    ...safeValue,
                                                                    option.value as MoodDescriptor,
                                                                ]);
                                                            } else {
                                                                onChange(
                                                                    safeValue.filter(
                                                                        (v) => v !== option.value
                                                                    )
                                                                );
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                    );
                                }}
                            />
                        </Stack>
                        <PageFooter includeShadow>
                            <Button
                                type="button"
                                variant={formValidation.hasMoodDescriptors ? 'contained' : 'text'}
                                color="primary"
                                onClick={handleNextStep}
                            >
                                {formValidation.hasMoodDescriptors
                                    ? 'Continue'
                                    : 'None of the above'}
                            </Button>
                        </PageFooter>
                    </>
                );
            case 'summaryNotes':
                return (
                    <>
                        <Stack sx={{ gap: 2 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                Tell us about your visit
                            </Typography>
                            <Controller
                                name="summaryNotes"
                                control={control}
                                shouldUnregister={false}
                                rules={{
                                    required: 'Please tell us about your visit',
                                }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        name={field.name}
                                        value={field.value || ''}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        ref={field.ref}
                                        multiline
                                        rows={4}
                                        placeholder="Add any additional notes about the visit..."
                                        fullWidth
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </Stack>
                        <PageFooter includeShadow>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleNextStep}
                                disabled={!formValidation.hasSummaryInput}
                            >
                                Continue
                            </Button>
                        </PageFooter>
                    </>
                );
            case 'supplyRequest': {
                const handleRemoveRequest = (index: number) => {
                    setSupplyRequests(supplyRequests.filter((_, i) => i !== index));
                };

                const handleKeyDown = (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddRequest();
                    }
                };

                return (
                    <>
                        <Stack sx={{ gap: 3, mb: 2 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                Does {careRecipientDisplayName}'s care require any additional
                                supplies?
                            </Typography>

                            <TextField
                                value={currentRequestDescription}
                                onChange={(e) => setCurrentRequestDescription(e.target.value)}
                                onKeyDown={handleKeyDown}
                                rows={3}
                                placeholder="Describe any supplies needed..."
                                fullWidth
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => handleAddRequest()}
                                                    disabled={!currentRequestDescription.trim()}
                                                    edge="end"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCirclePlus}
                                                        size="sm"
                                                    />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            {supplyRequests.length > 0 && (
                                <Stack sx={{ gap: 1 }}>
                                    <Typography variant="body2">Selected:</Typography>
                                    <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                                        {supplyRequests.map((request, index) => (
                                            <Stack
                                                key={index}
                                                direction="row"
                                                sx={{
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: spacing(1),
                                                    paddingLeft: spacing(2),
                                                    borderRadius: spacing(3),
                                                    backgroundColor: 'gray.950',
                                                    color: 'common.white',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ flex: 1 }}>
                                                    {request.requestDescription}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveRequest(index)}
                                                    sx={{ ml: 1 }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCircleXmark}
                                                        size="sm"
                                                        color={palette.common.white}
                                                    />
                                                </IconButton>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Stack>
                            )}
                            <Stack sx={{ gap: 1 }}>
                                <Typography variant="body2">Suggestions:</Typography>
                                <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                                    {requestSuggestions.map((request, index) => (
                                        <StyledChip
                                            key={index}
                                            label={request}
                                            onClick={() => handleAddRequest(request)}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        </Stack>
                        <PageFooter includeShadow>
                            <Button
                                type="button"
                                variant={supplyRequests.length > 0 ? 'contained' : 'text'}
                                color="primary"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleNextStep();
                                }}
                            >
                                {supplyRequests.length > 0 ? 'Continue' : 'Skip'}
                            </Button>
                        </PageFooter>
                    </>
                );
            }
            case 'confirm':
            default:
                return (
                    <>
                        <Stack sx={{ gap: 2, mb: 2 }}>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                Please Review Your Notes
                            </Typography>
                            <VisitSummaryDisplay
                                visitSummary={formValues}
                                supplyRequests={supplyRequests}
                                onEditSection={setCurrentStepId}
                            />
                        </Stack>
                        <PageFooter includeShadow>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                color="primary"
                                disabled={
                                    isLoading ||
                                    !formValidation.hasMood ||
                                    !formValidation.hasSummaryInput
                                }
                            >
                                {isLoading ? 'Saving...' : 'Save Summary'}
                            </Button>
                        </PageFooter>
                    </>
                );
        }
    };

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!visibleSteps.length) return;
        if (!visibleSteps.includes(currentStepId)) {
            setCurrentStepId(visibleSteps[0]);
        }
    }, [currentStepId, visibleSteps]);

    const onSubmit = async (data: AddVisitSummaryFormData) => {
        if (!careRecipient?.teamMemberRid || !visitRid) {
            showError('Missing required information');
            return;
        }

        try {
            // Create new summary (mood is required)
            await createVisitSummary({
                careRecipientRid: careRecipient.teamMemberRid,
                visitRid,
                createVisitSummaryRequest: {
                    mood: data.mood,
                    moodDescriptors:
                        data.moodDescriptors && data.moodDescriptors.length > 0
                            ? data.moodDescriptors
                            : undefined,
                    careRecipientObservations:
                        data.careRecipientObservations && data.careRecipientObservations.length > 0
                            ? data.careRecipientObservations
                            : undefined,
                    residenceObservations:
                        data.residenceObservations && data.residenceObservations.length > 0
                            ? data.residenceObservations
                            : undefined,
                    summaryNotes: data.summaryNotes || undefined,
                },
            }).unwrap();

            // Submit requests
            if (supplyRequests.length > 0) {
                await createRequest({
                    careRecipientRid: careRecipient.teamMemberRid,
                    createRequestsRequest: {
                        requests: supplyRequests,
                    },
                }).unwrap();
            }

            showSuccess('Visit summary saved successfully');
            navigate({ to: '/visits/summary/success' });
        } catch (error) {
            console.error('Error saving visit summary:', error);
            showError('Failed to save visit summary. Please try again.');
        }
    };

    return (
        <InnerMaxWidthContainer>
            <PageHeader
                hideBackButton={!canGoBack}
                onBack={canGoBack ? handlePreviousStep : undefined}
                actionButton={<StyledIconButton icon={faXmark} onClick={handleCloseClick} />}
            >
                <StyledLinearProgressBar value={(currentStepIndex / totalSteps) * 100} />
            </PageHeader>
            <form onSubmit={handleSubmit(onSubmit)}>
                {visibleSteps.map((step) => (
                    <Box key={step} sx={{ display: currentStepId === step ? 'block' : 'none' }}>
                        {renderStep(step)}
                    </Box>
                ))}
            </form>
        </InnerMaxWidthContainer>
    );
}
